import type { IndustryPage } from "@/lib/data";

export const carpenters: IndustryPage = {
  slug: "carpenters",
  seoTitle:
    "Websites for Carpenters and Joiners UK | Fixed £1,000",
  seoDescription:
    "Websites for carpenters and joiners: galleries of kitchens, wardrobes and staircases, plus local SEO. Fixed £1,000, live in 7 days. Book a call.",
  serviceName: "Website design for carpenters and joiners",
  tldr:
    "ConstructionWebDesign.uk builds websites for carpenters and joiners across the UK: mobile-first sites with galleries of your kitchens, wardrobes and staircases, a page per specialism and the trade badges you hold. The price is £1,000, one-off and fixed, with no subscription. Your website goes live in 7 days from the discovery call, and you own it.",
  pricingFromGBP: 1000,
  hero: {
    eyebrow: "Carpentry websites · UK",
    headline: "Website design for carpenters and joiners that shows your work.",
    highlight: "shows your work",
    sub: "Websites for carpenters and joiners with a photo-led project gallery, a page for each specialism, workshop and timber detail, a quote form and the Guild of Master Craftsmen or TrustMark badges you hold.",
    secondaryCta: {
      label: "See carpenter & joiner demos",
      href: "#demos",
    },
  },
  slides: [
    {
      bg: "https://constructionwebdesign.cdn.modulify.website/images/industry-hero/Carpenters%20%26%20Joiners.jpg",
      alt: "Carpenter & joiner hand-finishing a bespoke oak kitchen on the workshop bench",
      headlineTop: "Win bespoke joinery",
      headlineBottom: "work that lasts.",
      preview: "https://constructionwebdesign.cdn.modulify.website/images/demo-fullheight/joinwright.jpg",
    },
  ],
  demoCategory: "Carpenters & Joiners",
  demosHeading: "Websites for carpenters: demo templates.",
  demosSub:
    "Demo templates for carpenters and joiners, not client work. Open one to see the layout, gallery and page structure for bespoke kitchens, fitted wardrobes and staircases.",
  value: {
    eyebrow: "Why it matters",
    heading: "Why do carpenters and joiners need a website?",
    intro:
      "Carpenters and joiners need a website because homeowners want to see finished kitchens, wardrobes and staircases before they ask for a quote. A carpentry website shows that work, the trade badges you hold and the areas you cover, and takes enquiries while you're in the workshop.",
    points: [
      {
        title: "Show the work, properly photographed.",
        body: "Bespoke joinery sells on how it looks. A carpentry website shows your kitchens, wardrobes and staircases full width, with close-ups of the joints, the grain and the finish, so a homeowner can judge the quality of your work before they ring.",
        icon: "Plank",
      },
      {
        title: "Give every finished project its own page.",
        body: "Each kitchen, fitted wardrobe, staircase or piece of furniture gets a project page with photos, the timber used and the town. A homeowner gets a job like theirs to look at, and every project page gives Google another description of the work you do and where.",
        icon: "Sparkle",
      },
      {
        title: "Built for \"carpenter\" and \"joiner\" searches.",
        body: "Some homeowners search for a carpenter and others for a joiner, and the word often depends on where in the UK they live. A website for carpenters and joiners uses both words in page titles and headings, with on-page local SEO for the towns and postcodes you cover.",
        icon: "Pin",
      },
      {
        title: "Show the trade badges you hold.",
        body: "Guild of Master Craftsmen membership, City & Guilds or NVQ qualifications, TrustMark and your insurance cover reassure a homeowner before they commit to bespoke joinery. The site shows the ones you hold, with registration numbers linked to the official register where one exists, and leaves off any you don't.",
        icon: "Shield",
      },
    ],
  },
  features: {
    eyebrow: "What's in the site",
    heading: "What does website design for carpenters include?",
    intro:
      "Website design for carpenters from ConstructionWebDesign.uk includes a photo-led project gallery, a page for each specialism, workshop and timber detail, a quote form and the trade badges you hold. Every site is mobile-first, with on-page local SEO for the towns you cover.",
    items: [
      {
        title: "A photo-led project gallery.",
        body: "Every finished project gets its own page with full-width photos: a bespoke kitchen, fitted wardrobes, an oak staircase, a dining table or a garden room. Each page names the timber, the town and what the job involved, so a homeowner can see work like theirs and Google gets another page describing what you do and where.",
      },
      {
        title: "A page for each specialism.",
        body: "Bespoke kitchens, fitted wardrobes, staircases, internal, external and fire doors, skirting and architrave, garden rooms, decking, first and second fix, and hand-cut joinery each get their own page. Each one is written around what people type into Google, such as \"oak staircase\" or \"fitted wardrobes\" plus a town, and uses both carpenter and joiner.",
      },
      {
        title: "Workshop and timber detail.",
        body: "Pages that show you know the craft: oak, ash, walnut and tulipwood, mortise-and-tenon joints, dovetailed drawers, hand-finishing, and what happens in your workshop before anything is fitted on site. That detail shows the quality of your work before a homeowner asks about price.",
      },
      {
        title: "Price guides and a quote form.",
        body: "If you want to publish them, the site can show your own typical price ranges for jobs such as a fitted wardrobe, a bespoke kitchen or an oak staircase. The quote form asks for a postcode, a few photos and the scope of the job, so you can see what's involved before you call back. The figures are yours, and nothing goes live that you haven't approved.",
      },
      {
        title: "Trade badges near the top of the page, if you hold them.",
        body: "Guild of Master Craftsmen or Institute of Carpenters membership, City & Guilds or NVQ qualifications, TrustMark, FIRAS or BM TRADA Q-Mark certification for fire door installation, and your public liability insurance sit where a visitor looks first, with registration numbers linked to the official register where one exists. If you don't hold a scheme, its badge stays off the site.",
      },
      {
        title: "Mobile-first pages with on-page local SEO.",
        body: "Every page is designed for a phone first, so a homeowner can look through your projects and call in one tap. Page titles, headings and copy name your specialisms and the towns and postcodes you cover, using both carpenter and joiner, so the site can be found for either search.",
      },
    ],
  },
  faqs: [
    {
      q: "How much does a carpenter website cost in the UK?",
      a: "A carpenter or joiner website from ConstructionWebDesign.uk costs £1,000, a one-off fixed price with no subscription. The Website + Brand package costs £1,399 and adds a logo, visual identity and business card mockups. For comparison, typical UK ranges are about £4,000–£5,000 for a freelancer and £5,000–£25,000 for an agency, while Wix or Squarespace cost about £22 a month. You own the finished website.",
    },
    {
      q: "How long does it take to build a website for a carpenter?",
      a: "A website for a carpenter or joiner from ConstructionWebDesign.uk goes live in 7 days from the discovery call. The work runs in five steps: discovery call, strategy and sitemap, design, build, then launch and support. Having photos of finished projects, your list of specialisms and any Guild or TrustMark numbers ready for the call keeps the week on track.",
    },
    {
      q: "What should a carpenter website include?",
      a: "A carpenter website should include a photo-led gallery with a page per finished project, a page for each specialism (bespoke kitchens, fitted wardrobes, staircases, doors), workshop and timber detail, a quote form, a one-tap call button, reviews and the trade badges you hold, such as Guild of Master Craftsmen or TrustMark. Every page should work well on a phone and use both \"carpenter\" and \"joiner\".",
    },
    {
      q: "Do carpenters need a website?",
      a: "Yes, carpenters and joiners need a website, even as sole traders. Homeowners planning a bespoke kitchen, fitted wardrobes or a new staircase want to see finished work before they ask for a quote, and a website is where your project photos, trade badges, reviews and service areas sit together. Without one, a homeowner has only word of mouth or a directory listing to judge you on.",
    },
    {
      q: "How do websites for carpenters get found on Google?",
      a: "Websites for carpenters get found on Google through on-page local SEO, a separate page for each specialism and area, a complete Google Business Profile and steady customer reviews. Every carpentry website from ConstructionWebDesign.uk includes on-page local SEO, with page titles, headings and copy that name your specialisms, your towns and both \"carpenter\" and \"joiner\". Nobody can honestly guarantee a ranking, but that structure gives Google clear signals.",
    },
    {
      q: "Is web design for carpenters different from a builder website?",
      a: "Yes, web design for carpenters puts the finished work first, because bespoke joinery is chosen on how it looks. Carpenter website design from ConstructionWebDesign.uk leads with large project photos, gives each specialism its own page and explains the timber and joints you use. A general builder website usually leads with extensions, refurbishments and building accreditations instead.",
    },
    {
      q: "Do you build websites for joiners too?",
      a: "Yes, ConstructionWebDesign.uk builds websites for joiners as well as carpenters, at the same fixed £1,000 price. If your workshop calls itself a joinery, the headline, page titles and service pages can lead with \"joiner\" and \"joinery\", while still mentioning carpentry so the site can be found for both searches. Many firms in the North of England and Scotland describe themselves as joiners.",
    },
    {
      q: "Should a carpenter show prices on their website?",
      a: "Yes, a carpenter can show typical price ranges on their website, as long as the figures are their own. Ranges for common jobs, such as a fitted wardrobe, an oak staircase or a bespoke kitchen, let a homeowner check their budget before they get in touch, without tying you to a price before you've seen the space. On a ConstructionWebDesign.uk site, nothing goes live without your approval.",
    },
  ],
  guides: [
    {
      href: "/blog/how-much-should-a-construction-website-cost",
      label: "How much does a construction website cost in the UK?",
    },
    {
      href: "/blog/how-to-get-more-construction-leads",
      label: "How to get more construction leads without paying",
    },
    {
      href: "/blog/local-seo-for-builders",
      label: "Local SEO for builders: rank for \"builder near me\"",
    },
    {
      href: "/blog/google-business-profile-for-builders",
      label: "Google Business Profile for builders: setup guide",
    },
  ],
  finalCta: {
    headline: "Talk to us about your carpentry website.",
    highlight: "your carpentry website",
    sub: "Book a free 30-minute call to talk through your joinery work and the areas you cover. Fixed £1,000 price, and your website goes live in 7 days.",
  },
};
